import React from 'react';
import Joi from 'joi';
import _ from 'lodash';

import Button from '../themed/button';
import Conditional from '../common/conditional';
import DocumentNumberPreview from '../themed/document-number-preview';
import Progress from '../themed/progress';
import Form from '../common/form';
import StepIndicator from '../themed/step-indicator';

import { getAffirmativeOptions, getProjectTitleOptions } from '../../utils/data';

import * as authService from '../../services/authService';
import * as divisionService from '../../services/divisionService';
import * as projectService from '../../services/projectService';
import * as toastService from '../../services/toastService';

import * as utils from '../../utils/utilities';
import * as models from '../../utils/models';
import * as ids from '../../utils/ids'

import withInspectionTypes from '../hoc/withInspectionTypes';
import withOrganizations from '../hoc/withOrganizations';
import withProjects from '../hoc/withProjects';
import withUsers from '../hoc/withUsers';

import config from '../../utils/config';

interface FormData {
	projectTitle: string;
	equipmentDescription: string;
	inspectionTypeId: string;
	organisationId: string;
	divisionId: string;
	contactPersonId?: string;
	documentNumberPrefix: string;
	supervisorId: string;
	viewersId: string[];
	facilityId: string;
	clientReviewRequired: string;
	scope: string;
	location: string;
	module: string;
	equipmentTag: string;
	pidImages: models.PDFImageProps[];
}

interface FormProps {
	types: models.SelectOption[];
}

interface FormState {
	data: FormData;
	errors: object;
	loading: boolean;
	progress: number;
	step: number;
	division: models.Division | object;
}

class CreateProject extends Form {
	state = {
		data: initialData,
		errors: {},
		loading: false,
		progress: 0,
		step: 1,
		division: {},
	};

	schema = {
		projectTitle: Joi.string().required().label('Title'),
		scope: Joi.string().min(3).max(50).required().label('Scope'),
		inspectionTypeId: Joi.string().required().label('Inspection Type'),
		supervisorId: Joi.string().required().label('Inspector'),
		contactPersonId: Joi.string().allow(null).optional().label('Contact Person'),
		equipmentTag: Joi.string().min(4).required().label('Equipment Tag No'),
		facilityId: Joi.string().required().label('Facility'),
		organisationId: Joi.string().required().label('Organization ID'),
		clientReviewRequired: Joi.string().required().label('Client Review Required'),
		divisionId: Joi.string().required().label('Division ID'),
		documentNumberPrefix: Joi.string().when('divisionId', {
			is: ids.TOPSIDES_DIVISION_ID,
			then: Joi.string().max(this.props.project ? 255 : 50).required().label('Document Number Prefix'),
			otherwise: Joi.string().allow('').label('Document Number Prefix'),
		}),
		equipmentDescription: Joi.string().min(10).required().label('Description'),
		location: Joi.string().min(5).required().label('Location'),
		module: Joi.alternatives().try(Joi.object({
			label: Joi.string().required(),
			value: Joi.string().required(),
			__isNew__: Joi.boolean().optional(),
		}), Joi.string().min(5)).required().label('Module'),
		viewersId: Joi.array()
			.min(0)
			.items(
				Joi.object({
					id: Joi.string().required(),
					name: Joi.string().required(),
				})
			)
			.required()
			.label('Viewers'),
		pidImages: Joi.array().min(0).optional().label('P&ID Images'),
	};

	componentDidMount() {
		this.mapProjectToState();
	}

	componentDidUpdate(prevProps: Readonly<FormProps>, prevState: Readonly<FormState>, snapshot?: any): void {
		if (prevState.data.divisionId !== this.state.data.divisionId) {
			this.getDivisionDetails();
		}

		if (this.state.data.organisationId && prevState.data.facilityId !== this.state.data.facilityId) {
			if (this.state.data.organisationId === ids.PCNL_ID) {
				const selectedOrganization = this.getSelectedOrganization();
				if (selectedOrganization) {
					const selectedFacility = selectedOrganization.facilities.find(({ _id }) => _id === this.state.data.facilityId);
					if (selectedFacility) this.setState({ data: { ...this.state.data, module: selectedFacility.facilityName }});
				}
			}
		}

		if (this.state.data.organisationId !== prevState.data.organisationId) {
			if (this.state.data.organisationId !== ids.EXXONMOBIL_ID) return;

			this.setState({ data: { ...this.state.data, clientReviewRequired: 'yes' }})
		}
	}

	mapProjectToState = () => {
		const project: models.Project | null = this.props.project;
	
		if (project) {
			const mappedProjectDetails: any = {
				scope: project.scope,
				projectTitle: project.projectTitle,
				equipmentDescription: project.equipmentDescription,
				inspectionTypeId: project.inspectionTypeId,
				organisationId: project.organisationId,
				contactPersonId: project.contactPersonId,
				supervisorId: project.supervisorId,
				divisionId: project.divisionId,
				documentNumberPrefix: project.documentNumber,
				module: { label: project.module, value: project.module },
				viewersId: this.getViewerDetails(),
				facilityId: project.facilityId,
				location: project.location,
				equipmentTag: project.equipmentTag,
				pidImages: utils.getPIDImages(project.pidImages),
			}
	
			this.setState({ data: mappedProjectDetails });
		}
	};

	getViewerDetails = (): models.SelectOption[] => {
		const project: models.Project | null = this.props.project;
		if (!project) return [];

		return project.viewersId.map((id: string, i: number) => {
			return ({ id, name: project.viewersName[i] })
		});
	};

	getPIDImageURLs = (): string[] => {
		return this.state.data.pidImages.map((image) => image.src);
	};

	getModuleValue = (): string => {
		const {data} = this.state as any;

		return (_.isObject(data.module)) ? data.module.value : data.module;
	}

	getApiData = () => {
		const apiData: any = { ...this.state.data };
		if (!apiData.divisionId) {
			return toastService.error('Please select a division first!');
		}

		if (apiData.viewersId.length > 0) {
			apiData.viewersId = utils.transformSelectOptions(apiData.viewersId);
		} else {
			delete apiData.viewersId;
		}

		apiData.documentNumberPrefix = apiData.documentNumberPrefix.toUpperCase();
		apiData.pidImages = this.getPIDImageURLs();
		apiData.module = this.getModuleValue();
		apiData.clientReviewRequired = utils.matchPhrases(this.state.data.clientReviewRequired, 'yes');

		return apiData;
	};

	getIsTopsidesDivision = () => {
		return this.state.data.divisionId === ids.TOPSIDES_DIVISION_ID;
	};

	getSelectedFacility = (): models.Facility | undefined => {
		const organization = this.getSelectedOrganization();
		if (!organization) return;

		return organization.facilities.find((f) => f._id === this.state.data.facilityId);
	};

	getSelectedOrganization = (): models.Organization | undefined => {
		const allOrganizations: models.Organization[] = this.props.organizations;
		return allOrganizations.find((o) => o._id === this.state.data.organisationId);
	};

	getSelectedInspectionType = () => {
		return this.props.types.find((t: models.SelectOption) => t.id === this.state.data.inspectionTypeId);
	};

	getDivisionDetails = async (): Promise<void> => {
		this.setState({ loading: true });

		try {
			const { divisionId } = this.state.data;
			const { data } = await divisionService.getOneDivision(divisionId);

			this.setState({ loading: false, division: data.division } as any);
		} catch (ex) {
			this.setState({ loading: false });
		}
	};

	getDocumentNumber = (): string | null => {
		const organization = this.getSelectedOrganization();
		if (!organization) return null;

		const facility = this.getSelectedFacility();
		if (!facility) return null;

		const inspectionType = this.getSelectedInspectionType();
		if (!inspectionType) return null;

		const currentYear: number = new Date().getFullYear();
		const totalProjects: number = this.props.totalProjects + 1;
		const organizationPrefix = organization.organisationName.substring(0, 3).trim();
		const facilityPrefix = facility.facilityName.substring(0, 3).trim();
		const inspectionPrefix: string = inspectionType.code;

		const commonPrefix = `${currentYear}/RSG/${organizationPrefix}/${facilityPrefix}/${inspectionPrefix}`;
		const { documentNumberPrefix } = this.state.data;
		if (this.getIsTopsidesDivision()) {
			return `${commonPrefix}/${documentNumberPrefix.split(' ').join('-')}/${totalProjects.toString().padStart(3, '0')}`.toUpperCase();
		}

		return `${commonPrefix}/${totalProjects.toString().padStart(3, '0')}`.toUpperCase();
	};

	getFacilitiesByOrganization = (): models.SelectOption[] => {
		const organization = this.getSelectedOrganization();
		if (!organization) return [];

		return organization.facilities.filter((facility) => facility.isActive).map(utils.transformFacilityItem);
	};

	getInspectionTypes = (): models.SelectOption[] => {
		const division = this.state.division as models.Division;
		if (division && division.inspectionType) {
			return division.inspectionType.map(utils.transformInspectionType);
		}

		return [];
	};

	getUsersInOrganization = (users: models.User[]) => {
		return (
			users
			.filter((user) => user.organisationId === this.state.data.organisationId)
			.map(utils.transformUserItem)
		);
	};

	getOrganizationContactPersons = (): models.SelectOption[] => {
		const organization = this.getSelectedOrganization();
		if (organization) return this.getUsersInOrganization(organization.contactPersons);
		
		return [];
	};

	getOrganizationViewers = (): models.SelectOption[] => {
		const selectedOrganization = this.getSelectedOrganization();
		if (selectedOrganization) return this.getUsersInOrganization(selectedOrganization.viewers);

		return [];
	};

	handleCreateModule = (module: string) => {
		this.setState({
			data: { ...this.state.data, module: { label: module, value: module } },
		});
	};

	getSupervisors = (): models.SelectOption[] => {
		const users: models.User[] = (this.state.division as any).users ?? [];
		const supervisors: models.SelectOption[] = this.props.supervisors;

		const userIds = users.map((user) => user.id);
		return supervisors.filter((s) => userIds.includes(s.id));
	};

	getUserDivisions = (): models.SelectOption[] => {
		const user = authService.getCurrentUser() as models.User;
		return user.division.map((id: string, index: number) => ({
			id,
			name: user.divisionName[index],
		}));
	};

	handleSetStep = (step: number) => {
		this.setState({ step } as FormState);
	};

	postData = async (): Promise<void> => {
		const apiData = this.getApiData();

		if (this.props.project) {
			await projectService.updateReport(this.props.project._id, apiData, this.handleProgressChange);
		} else {
			await projectService.createProject(apiData, this.handleProgressChange);
		}

		window.location.reload();
	};

	getAvailableModules = () => {
		const facility = this.getSelectedFacility();
		if (!facility) return [];

		return facility.modules.map((module) => ({ label: module.name, value: module.name }));
	};

	doSubmit = (): void => {
		const prefix = this.props.project ? 'Upda' : 'Crea';
		toastService.promise(this.postData(), {
			loading: `${prefix}ting Project`,
			success: () => `Project ${prefix}ted`,
			error: utils.getErrorMessage,
		});
	};

	render() {
		return (
			<React.Fragment>
				<Progress progress={this.state.progress} />

				<Conditional isVisible={this.getIsTopsidesDivision()}>
					<StepIndicator
						progress={this.state.step === 1 ? 25 : 75}
						totalSteps={2}
						activeStep={this.state.step}
					/>
				</Conditional>

				<form
					autoComplete="off"
					onSubmit={this.handleSubmit}
					className="p-3 container"
				>
					<Conditional isVisible={this.state.step === 1}>
						<h1 className="form-title">Create Project</h1>

						<p className="form-subtitle">
							Complete the details below to create a project
						</p>

						<div className="row">
							<div className="col">
								{this.renderSelect({
									name: 'divisionId',
									label: 'Division',
									placeholder: 'Select Division',
									options: this.getUserDivisions(),
								})}
							</div>

							<div className="col">
								{this.renderSelect({
									name: 'projectTitle',
									label: 'Project Title',
									placeholder: 'Select Inspection',
									options: getProjectTitleOptions(),
								})}
							</div>
							
							<div className="col">
								{this.renderInput({
									type: 'text',
									name: 'scope',
									label: 'Project Scope',
									placeholder: 'Enter Project Scope',
								})}
							</div>
						</div>

						<div className="divider my-4"></div>

						<div className="row">
							<div className="col">
								{this.renderSelect({
									name: 'organisationId',
									label: 'Organization',
									placeholder: 'Select Organization',
									options: this.props.organizationsForDropdown,
								})}
							</div>

							<div className="col">
								{this.renderSelect({
									name: 'facilityId',
									label: 'Facility',
									placeholder: 'E.g. USAN FPSO',
									options: this.getFacilitiesByOrganization(),
								})}
							</div>

							<Conditional isVisible={config.enableContactPerson}>
								<div className="col">
									{this.renderSelect({
										name: 'contactPersonId',
										label: 'Contact Person',
										placeholder: 'Select Contact Person',
										options: this.getOrganizationContactPersons(),
									})}
								</div>
							</Conditional>
						</div>


						<Conditional isVisible={false}>
							<div className="divider my-4"></div>
							
							{this.renderMultiSelect({
								options: this.getOrganizationViewers(),
								name: 'viewersId',
								placeholder: 'Select Viewers',
								label: 'Viewers',
								showCheckbox: true,
								loading: false,
							})}
						</Conditional>

						<div className="divider my-4"></div>

						<div className="row">
							<div className="col">
								{this.renderSelect({
									name: 'inspectionTypeId',
									label: 'Inspection Type',
									placeholder: 'Select Inspection Type',
									options: this.getInspectionTypes(),
								})}
							</div>

							<div className="col">
								{this.renderTextArea({
									name: 'equipmentDescription',
									label: 'Description',
									placeholder: 'E.g. Agbara Helideck',
									rows: 3,
								})}
							</div>
						</div>

						<div className="divider my-4"></div>

						<div className="row">
							<div className="col">
								{this.renderInput({
									type: 'text',
									name: 'equipmentTag',
									label: 'Equipment Tag',
									placeholder: 'E.g. AGH-61637',
								})}
							</div>

							<div className="col">
								<div>
									{this.renderInput({
										type: 'text',
										name: 'location',
										label: 'Location',
										placeholder: 'E.g. Agbara Platform',
									})}
								</div>
							</div>

							<div className="col">
								{this.renderCreatableMultiselect({
									closeMenuOnSelect: true,
									options: this.getAvailableModules(),
									name: 'module',
									placeholder: 'Enter Module',
									label: 'Module',
									isMulti: false,
								})}
							</div>
						</div>

						<div className="divider my-4"></div>

						{this.renderSelect({
							name: 'supervisorId',
							label: 'Assign To Inspector',
							placeholder: 'Select Inspector',
							options: this.getSupervisors(),
						})}

						<div className="divider my-4"></div>

						<div className="d-flex justify-content-end">
							{this.getIsTopsidesDivision() ? (
								<Button
									label="Next"
									type="button"
									onClick={() => this.handleSetStep(2)}
								/>
							) : (
								this.renderButton(this.state.loading ? 'Uploading' : 'Submit')
							)}
						</div>
					</Conditional>

					<Conditional isVisible={this.state.step === 2}>
						<h2 className="form-title">Document Number Prefix</h2>

						<p className="form-subtitle">Enter document number prefix</p>

						<DocumentNumberPreview documentNumber={this.props.project ? this.props.project.documentNumber : this.getDocumentNumber()} />

						<div className="divider my-4"></div>

						<div className="row">
							<div className="col">
								{this.renderSelect({
									disabled: this.state.data.organisationId === ids.EXXONMOBIL_ID,
									name: 'clientReviewRequired',
									label: 'Client Review Required',
									placeholder: 'Select Option',
									options: getAffirmativeOptions(),
								})}
							</div>
							
							<div className="col">
								{this.renderInput({
									type: 'text',
									name: 'documentNumberPrefix',
									label: 'Document Number Prefix',
									readOnly: Boolean(this.props.project) ,
									placeholder: 'E.g. Module P Piping (Part 01)',
								})}
							</div>
						</div>

						<div className="d-flex justify-content-end">
							<Button
								label="Previous"
								type="button"
								style={{ backgroundColor: '#ab3c3c', marginRight: '0.5rem' }}
								onClick={() => this.handleSetStep(1)}
							/>

							{this.renderButton(this.state.loading ? 'Uploading' : 'Submit')}
						</div>
					</Conditional>
				</form>
			</React.Fragment>
		);
	}
}

const initialData: FormData = {
	projectTitle: '',
	equipmentDescription: '',
	inspectionTypeId: '',
	documentNumberPrefix: '',
	organisationId: '',
	divisionId: '',
	supervisorId: '',
	clientReviewRequired: '',
	viewersId: [],
	facilityId: '',
	location: '',
	module: '',
	scope: '',
	equipmentTag: '',
	pidImages: [],
};

const Projects = withProjects(CreateProject);
export default withUsers(withOrganizations(withInspectionTypes(Projects)));
